/**
 * Shared service for Checkout / DryRunShiftPlan API operations.
 * Used by both the Volumes page (initial baseline preview) and the Checkout page (apply changes).
 */
import { store } from '../../store';
import { apiRequest } from '../apiMiddleware';
import { API_GATEWAY_PROCESS_NAME } from '../../utils/constants';
import { showError } from '../../store/slices/errorSlice';
import { selectStationCode } from '../../store/slices/shiftPlanSlice';
import {
  DryRunShiftPlanRequest,
  DryRunShiftPlanResponse,
} from '../../pages/checkout/model';
import {
  logOperationalMetric,
  MetricType,
  RCC_SHIFT_PLAN_CHECKOUT_TAB,
  DRY_RUN_SHIFT_PLAN_API,
} from '../../utils/metric-utils';

/**
 * Common error handler for dryRunShiftPlan
 */
const handleDryRunShiftPlanError = (errorSource: any, startTime: number, stationCode: string): null => {
  const errorMessage =
    errorSource.errorResponse ||
    errorSource.message ||
    'Unable to load the plan preview. Please try again later.';

  logOperationalMetric(startTime, stationCode, RCC_SHIFT_PLAN_CHECKOUT_TAB, DRY_RUN_SHIFT_PLAN_API, MetricType.FAILED, errorSource);

  store.dispatch(
    showError({
      title: 'Failed to Load Plan Preview',
      message: errorMessage,
    })
  );

  return null;
};

/**
 * Execute a dry-run of the shift planning algorithm to produce a
 * per-process-path headcount preview for the Checkout page.
 *
 * @param request - Plan identity fields and optional output adjustments
 * @returns DryRunShiftPlanResponse with per-path breakdown, or null on error
 */
export const dryRunShiftPlan = async (
  request: DryRunShiftPlanRequest
): Promise<DryRunShiftPlanResponse | null> => {
  const startTime = Date.now();
  const stationCode = selectStationCode(store.getState()) as string;
  try {
    const response = await apiRequest({
      resourcePath: '/dryRunShiftPlan',
      httpMethod: 'post',
      processName: API_GATEWAY_PROCESS_NAME,
      requestBody: request,
    });

    if (response.status && response.status >= 400) {
      return handleDryRunShiftPlanError(response, startTime, stationCode);
    }

    logOperationalMetric(startTime, stationCode, RCC_SHIFT_PLAN_CHECKOUT_TAB, DRY_RUN_SHIFT_PLAN_API, MetricType.SUCCESS);
    return response.data;
  } catch (error: any) {
    console.error('Error calling DryRunShiftPlan:', error);
    return handleDryRunShiftPlanError(error, startTime, stationCode);
  }
};
